import { readTodayWorkQuota, type TodayWorkQuota } from './todayWorkQuota'

/** 与客户端独立权益 iframe 对应，不复用业务菜单消息，避免污染菜单权限。 */
export const TODAY_WORK_PRIVILEGES_SOURCE = 'todaywork-privileges-v1'
export const TODAY_WORK_PRIVILEGES_FRAME = 'todaywork-privileges'

export interface TodayWorkPrivilegesRequest {
  source: typeof TODAY_WORK_PRIVILEGES_SOURCE
  type: 'request'
  /** 父窗明确支持时才委托开通，兼容旧客户端。 */
  openMembershipSupported?: boolean
  requestId: string
  userId: string
  groupId: string
  /** 展示字段不参与会员鉴权或实际积分授予。 */
  quota?: TodayWorkQuota
}

export interface TodayWorkPrivilegesState {
  /** 路由内容已挂载、初始化和团队校验已完成。 */
  ready: boolean
  /** 初始化或登录承接流程已确认的失败原因。 */
  error: string
  /** 当前真实登录账号，仅在本地比较，不回传不匹配账号的信息。 */
  userId: string
  /** 当前真实团队。 */
  groupId: string
}

/**
 * 校验直接父窗口的权益页状态请求，拒绝子框架、站外消息和无效身份字段。
 * @param event - 浏览器消息事件
 * @param parent - 当前 iframe 的直接父窗口
 * @returns 校验通过的请求，否则返回 null
 * @example readTodayWorkPrivilegesRequest(event, window.parent)
 */
export function readTodayWorkPrivilegesRequest(
  event: Pick<MessageEvent, 'source' | 'origin' | 'data'>,
  parent: Window
): TodayWorkPrivilegesRequest | null {
  if (
    event.source !== parent ||
    ![
      'app://local',
      'null',
      'http://localhost:5273',
      'http://127.0.0.1:5273'
    ].includes(event.origin)
  )
    return null
  const data: unknown = event.data
  if (!data || typeof data !== 'object') return null
  const message = data as Record<string, unknown>
  if (
    message.source !== TODAY_WORK_PRIVILEGES_SOURCE ||
    message.type !== 'request' ||
    typeof message.requestId !== 'string' ||
    !/^[a-zA-Z0-9_-]{16,96}$/.test(message.requestId) ||
    typeof message.userId !== 'string' ||
    !/^[a-zA-Z0-9_-]{1,128}$/.test(message.userId) ||
    typeof message.groupId !== 'string' ||
    !/^[a-zA-Z0-9_-]{1,128}$/.test(message.groupId)
  )
    return null
  return {
    source: TODAY_WORK_PRIVILEGES_SOURCE,
    type: 'request',
    openMembershipSupported: message.openMembershipSupported === true,
    requestId: message.requestId,
    userId: message.userId,
    groupId: message.groupId,
    ...(message.quota && typeof message.quota === 'object'
      ? { quota: readTodayWorkQuota(message.quota) }
      : {})
  }
}

/**
 * 只在真实账号和团队都匹配时报告就绪；错误回执不泄露其他团队身份。
 * @param request - 已校验的父窗请求
 * @param state - 当前路由和登录状态
 * @returns 带请求标识的状态回执
 * @example buildTodayWorkPrivilegesReply(request, { ready: true, error: '', userId: 'u', groupId: 'g' })
 */
export function buildTodayWorkPrivilegesReply(
  request: TodayWorkPrivilegesRequest,
  state: TodayWorkPrivilegesState
) {
  const error =
    state.error ||
    (state.userId && state.userId !== request.userId
      ? 'account_mismatch'
      : '') ||
    (state.ready && state.groupId !== request.groupId ? 'team_unavailable' : '')
  return {
    ...request,
    type: 'state',
    status: error
      ? 'error'
      : state.ready &&
          state.userId === request.userId &&
          state.groupId === request.groupId
        ? 'ready'
        : 'loading',
    error
  }
}
